import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, Input, OnDestroy, OnInit, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { faClose } from '@fortawesome/pro-light-svg-icons';

import { BehaviorSubject, ReplaySubject, Subscription, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { SystemService } from '@mom/shared/data';

@Component({
  selector: 'eiq-tags-input',
  templateUrl: './tags-input.component.html',
  styleUrls: ['./tags-input.component.scss']
})
export class TagsInputComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @ViewChild('tagsInput') tagInput: ElementRef<HTMLInputElement>;

  @Input() required: boolean;

  usedTags$ = this.systemService.getUsedTags();

  separatorKeysCodes: number[] = [ENTER, COMMA];
  faClose = faClose;
  tags: string[] = [];
  tagCtrl = new FormControl();

  onChange: (value: string[]) => void;
  onTouched: () => void;

  private input$ = new ReplaySubject(1);
  private usedTagsEvent$ = new BehaviorSubject<string>(null);

  private subscriptions: Subscription[] = [];
  private usedTags: string[] = [];

  constructor(
    @Self() public ngControl: NgControl,
    private systemService: SystemService
  ) {
    this.ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.subscriptions.push(
      this.input$.subscribe((input: string[]) => {
        this.onTouched();
        this.onChange(input);
      })
    );

    this.subscriptions.push(this.tagCtrl.valueChanges.subscribe((value) => this.usedTagsEvent$.next(value)));

    this.usedTags$ = this.usedTagsEvent$.pipe(
      switchMap((filter: string) => {
        if (filter) {
          return of(this.filter(filter));
        }
        return this.systemService.getUsedTags().pipe(tap((tags) => (this.usedTags = tags)));
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  writeValue(input: string[]): void {
    this.tags = input;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(_isDisabled: boolean): void {}

  onAddTag(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
    if (value) {
      this.tags.push(value);
      this.input$.next(this.tags);
    }

    event.chipInput.clear();
    this.tagCtrl.setValue(null);
  }

  onRemove(index: number): void {
    this.tags.splice(index, 1);
    this.input$.next(this.tags);
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.tags.push(event.option.viewValue);
    this.input$.next(this.tags);

    this.tagInput.nativeElement.value = '';
    this.tagCtrl.setValue(null);
  }

  private filter(value: string): string[] {
    return this.usedTags.filter((tag) => tag.toLowerCase().includes(value.toLowerCase()));
  }
}
